import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ROUTES from '../routes.json';
import { normalizeRoute } from '../utils/routeHelpers';
import PageHelmet from '../components/PageHelmet';

export default function CaptionGenerator() {
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('Friendly');
  const [isLoading, setIsLoading] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [error, setError] = useState(null);
  
  // Head handled with Helmet via PageHelmet component
  
  const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^['"]+|['"]+$/g, '').trim().replace(/\/+$/,'');
  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setCaptions([]);
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/captions/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic, tone }),
      });

      const contentType = res.headers.get('content-type') || '';
      if (!res.ok) {
        // Prefer JSON error body with details from backend
        if (contentType.includes('application/json')) {
          const errBody = await res.json().catch(() => ({ error: 'Server error' }));
          const detailText = errBody.details || errBody.error || errBody.message || JSON.stringify(errBody);
          throw new Error(detailText);
        } else {
          const text = await res.text().catch(() => '');
          throw new Error(text ? `Server error: ${text.substring(0,200)}` : `Server error: ${res.status}`);
        }
      }

      if (contentType.includes('application/json')) {
        const data = await res.json();
        if (data && data.captions) setCaptions(data.captions);
        else setError('No captions returned from server.');
      } else {
        const text = await res.text();
        throw new Error(`Unexpected non-JSON response: ${text.substring(0,200)}`);
      }
    } catch (err) {
      setError(err.message || 'Request failed (network or proxy error)');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="app-root">
      <PageHelmet
        title="Caption Generator — Social Captions | OneAIHuman"
        description="Generate engaging, human-friendly captions for social posts, ads, and image descriptions using AI suggestions you can edit and reuse quickly."
        canonical={window.location.origin + ROUTES.captionGenerator.path}
      />
      <div className="app-card">
        <div className="header-sub">
          <h1 className="page-title">Caption Generator</h1>
          <p className="page-sub">Human-guided short captions that feel like a person wrote them.</p>
        </div>

        {/* SEO content: explain tool, benefits, and quick highlights */}
        <div className="page-content" style={{ marginTop: 12, color: '#6b7280' }}>
          <p>
            The Caption Generator helps you craft concise, engaging captions for social posts, ads, and image descriptions. Describe the image or topic and choose a tone; the tool will produce several caption variants designed to capture attention and match your brand voice. This is ideal for social media managers, small teams, or creators who need publish-ready text fast without losing personality.
          </p>
          <p>
            Using short, human-friendly language improves click-through and saves editing time. Outputs are designed to be editable and copy-ready so you can quickly A/B test several captions or reuse them across platforms.
          </p>
          <ul style={{ marginTop: 8 }}>
            <li>Generate multiple caption variants tailored for different networks.</li>
            <li>Suggests tone, hashtags, and length optimized for engagement.</li>
            <li>Quick, copy-ready outputs for faster publishing.</li>
          </ul>
        </div>

        <div className="main">
          <div className="tool-area">
            <div className="panel panel-left">
              <form onSubmit={handleSubmit} className="tool-controls" style={{gap:12,alignItems:'center'}}>
                <div style={{flex:1}}>
                  <label style={{display:'block',marginBottom:8,fontWeight:600}}>Topic</label>
                  <input
                    className="input-area"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g. Chocolate Cake Shop"
                    required
                  />
                </div>

                <div style={{width:220}}>
                  <label style={{display:'block',marginBottom:8,fontWeight:600}}>Tone</label>
                  <select className="input-area" value={tone} onChange={(e) => setTone(e.target.value)}>
                    <option>Friendly</option>
                    <option>Professional</option>
                    <option>Funny</option>
                    <option>Inspirational</option>
                  </select>
                </div>

                <div style={{display:'flex',alignItems:'flex-end'}}>
                  <button className="btn primary" type="submit" disabled={isLoading}>
                    {isLoading ? 'Generating...' : 'Generate Captions'}
                  </button>
                </div>
              </form>

              {error && <div className="hero-caption" style={{color:'crimson',marginTop:12}}>Error: {error}</div>}
            </div>

            <div className="panel">
              <h2 style={{marginTop:0}}>Captions</h2>
              {captions.length === 0 && <div className="hero-caption">No captions yet — enter a topic and generate.</div>}
              {captions.length > 0 && (
                <ul style={{marginTop:8}}>
                  {captions.map((c, i) => (
                    <li key={i} className="hero-caption">{c}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    
      <div style={{ marginTop: 20, padding: 8 }}>
        <h4>Explore more</h4>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to={normalizeRoute(ROUTES.humanizer.path)} className="btn ghost">Try the Humanizer</Link>
          <Link to={normalizeRoute(ROUTES.blog.path)} className="btn ghost">Visit our Blog</Link>
          <Link to={normalizeRoute(ROUTES.contact.path)} className="btn ghost">Contact Us</Link>
        </div>
      </div>
    </div>
  );
}